/**
 * Under the viewer, like the desktop app: "Kiểm tra chuẩn" (QC items, folded by default behind a summary and the
 * verdict badge), notes (always shown: they carry the "not for passports" warnings), then download / delete. An AI job
 * offers both photos: the AI one and the same photo processed without AI (`files.local`).
 */
import { useId, useState } from "react";
import { RateResult } from "./RateResult";
import { ApiError, type Api, type Job, type QCItem } from "../api";
import {
  AI_RESULT, CONTINUE, QC_ITEM_HINTS, QC_ITEM_LABELS, QC_ITEM_NAMES, QC_LABELS, errorMessage, label, warningMessages,
} from "../messages";
import type { Paper } from "../sheet";
import { PrintDialog, type SheetPart } from "./PrintDialog";
import { ShareResult } from "./ShareResult";

interface Props {
  api: Api;
  job: Job | null;
  pollError: string | null;
  onDeleted: (job: Job) => void;
  /** the user rated the shown photo */
  onRated?: (job: Job) => void;
  /** size of the job's photo spec, for the print sheet */
  photoMm?: { width: number; height: number };
  papers?: Paper[];
  /** "Chỉnh tiếp ảnh này": the result opens in "Ảnh chuyên nghiệp" (rejects when the photo cannot be fetched) */
  onContinue?: (job: Job) => Promise<void>;
  /** GĐ4: every spec of the job, for one print sheet with some of each */
  parts?: SheetPart[];
  /** GĐ4: the job of several specs (the files of every spec), when `job` shows one of them */
  sheetJob?: Job;
}

const ORDER: Record<string, number> = { fail: 0, warn: 1, pass: 2 };
const ICON: Record<string, string> = { pass: "✓", warn: "!", fail: "✕" };

function qcItems(job: Job): QCItem[] {
  const items = job.qc?.items;
  if (items && items.length > 0) return [...items].sort((a, b) => (ORDER[a.level] ?? 3) - (ORDER[b.level] ?? 3));
  // older render machines only sent the warn/fail sentences
  return (job.qc?.messages ?? []).map((m, i) => ({ code: `msg-${i}`, level: "warn", message: m }));
}

/** "12:45 ngày 21/10" (local time). */
function deleteTime(iso: string): string {
  const d = new Date(iso);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(d.getHours())}:${two(d.getMinutes())} ngày ${two(d.getDate())}/${two(d.getMonth() + 1)}`;
}

/** "12 mục · 1 lưu ý" / "12 mục · 2 lỗi, 1 lưu ý" / "12 mục đều đạt". */
function summary(items: QCItem[]): string {
  const fail = items.filter((i) => i.level === "fail").length;
  const warn = items.filter((i) => i.level === "warn").length;
  const parts = [fail && `${fail} lỗi`, warn && `${warn} lưu ý`].filter(Boolean);
  return parts.length ? `${items.length} mục · ${parts.join(", ")}` : `${items.length} mục đều đạt`;
}

export function ResultPanel({ api, job, pollError, onDeleted, onRated, photoMm, papers, onContinue, parts,
                             sheetJob }: Props) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [continueFailed, setContinueFailed] = useState(false);
  const bodyId = useId();

  async function cont() {
    if (!job || !onContinue || continuing) return;
    setContinuing(true);
    setContinueFailed(false);
    try {
      await onContinue(job);
    } catch (e) {
      console.warn("result photo unavailable for the editor", e);
      setContinueFailed(true);
    } finally {
      setContinuing(false);
    }
  }

  async function remove() {
    if (!job) return;
    setDeleting(true);
    setError(null);
    try {
      onDeleted(await api.deleteJob(job.id));
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "network"));
    } finally {
      setDeleting(false);
    }
  }

  const done = job?.status === "done";
  const items = job && done ? qcItems(job) : [];
  const notes = job ? warningMessages(job.warnings) : [];
  const level = done ? job?.qc?.level : undefined;

  return (
    <section className="card result-panel" aria-labelledby="qc-title">
      <div className="card-head">
        <h2 id="qc-title" className="qc-title">
          {items.length > 0 ? (
            <button type="button" className="qc-toggle" aria-expanded={open} aria-controls={bodyId}
                    onClick={() => setOpen((v) => !v)}>
              <span className="chevron" aria-hidden="true">{open ? "▾" : "▸"}</span>
              Kiểm tra chuẩn
              <span className="qc-count">{summary(items)}</span>
            </button>
          ) : "Kiểm tra chuẩn"}
        </h2>
        {level && (
          // key: the badge animates again when the verdict changes
          <span key={`${job?.id}-${level}`} role="status" className={`qc-badge qc-badge-${level}`}>
            <span className="qc-icon" aria-hidden="true">{ICON[level]}</span>
            <span>{label(QC_LABELS, level)}</span>
          </span>
        )}
      </div>

      {!job && !pollError && (
        <p className="hint">Sau khi render, danh sách kiểm tra (cỡ đầu, đường mắt, nghiêng, nền…) và nút tải ảnh hiện ở đây.</p>
      )}
      {job && !done && job.status !== "rejected" && job.status !== "failed" && job.status !== "cancelled" && (
        <p className="hint">Đang chờ kết quả…</p>
      )}

      {items.length > 0 && (
        <ul id={bodyId} className="qc-list" hidden={!open}>
          {items.map((it) => (
            <li key={`${it.code}-${it.message}`} className={`qc-item qc-${it.level}`}>
              <span className="qc-tag">{label(QC_ITEM_LABELS, it.level)}</span>
              <span>
                {QC_ITEM_NAMES[it.code] ?? it.message}
                {QC_ITEM_NAMES[it.code] && it.level !== "pass" && QC_ITEM_HINTS[it.code] && (
                  <span className="qc-hint">: {QC_ITEM_HINTS[it.code]}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {notes.length > 0 && (
        <div className="notes-box">
          <h3>Ghi chú</h3>
          <ul className="notes">{notes.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      {job && (
        <div className="result-actions">
          {done && job.files.photo && (
            <div className="actions">
              <a className="btn btn-primary" href={api.fileUrl(job.files.photo)} download>
                {job.files.local ? AI_RESULT.downloadAi : "Tải ảnh JPG"}
              </a>
              {job.files.local && (
                <a className="btn" href={api.fileUrl(job.files.local)} download>{AI_RESULT.downloadLocal}</a>
              )}
              {photoMm && <button type="button" className="btn" onClick={() => setPrinting(true)}>Tải tờ in PDF…</button>}
              <ShareResult url={api.fileUrl(job.files.photo)} name={`anh-the-${job.id.slice(0, 8)}.jpg`} />
            </div>
          )}
          {done && job.files.photo && job.files.local && <p className="hint">{AI_RESULT.hint}</p>}
          {done && job.files.photo && onContinue && (
            <div className="continue-box">
              <button type="button" className="btn" onClick={() => void cont()} disabled={continuing}>
                {continuing ? CONTINUE.busy : CONTINUE.button}
              </button>
              <p className="hint">{CONTINUE.hint}</p>
              {continueFailed && <p role="alert" className="error">{CONTINUE.failed}</p>}
            </div>
          )}
          {done && job.files.photo && (
            <p className="hint">
              Ảnh tự xoá lúc {deleteTime(job.expires_at)}.
            </p>
          )}
          {done && job.files.photo && onRated && (
            <RateResult api={api} job={job} onRated={(feedback) => onRated({ ...job, feedback })} />
          )}
          {printing && photoMm && (
            <PrintDialog api={api} job={parts && parts.length > 1 && sheetJob ? sheetJob : job} photoMm={photoMm}
                         papers={papers} parts={parts} onClose={() => setPrinting(false)} />
          )}
          {job.status !== "running" && job.status !== "cancelled" && !(done && !job.files.photo) && (
            <button type="button" className="btn btn-quiet" onClick={() => void remove()} disabled={deleting}>
              {job.status === "queued" ? "Huỷ (hoàn token)" : "Xoá ảnh ngay"}
            </button>
          )}
        </div>
      )}
      {(error || pollError) && <p role="alert" className="error">{error ?? errorMessage(pollError as string)}</p>}
    </section>
  );
}
